import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';

const storageKey = (accountId, tourId) => `proovam_tour_v1:${encodeURIComponent(accountId)}:${tourId}`;

const readState = (key) => {
  if (!key) return null;
  try {
    return JSON.parse(localStorage.getItem(key) || 'null');
  } catch {
    return null;
  }
};

const GuidedTour = ({ accountId, tourId, steps = [], className = '' }) => {
  const key = accountId ? storageKey(accountId, tourId) : null;
  const [state, setState] = useState(() => readState(key));
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const cardRef = useRef(null);

  useEffect(() => {
    setState(readState(key));
    setOpen(false);
    setIndex(0);
  }, [key]);

  useEffect(() => {
    if (!open || !steps[index]) return undefined;
    const anchor = document.querySelector(`[data-tour-id="${steps[index].anchor}"]`);
    anchor?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    anchor?.classList.add('ring-2', 'ring-brand-400', 'ring-offset-2');
    const place = () => {
      if (!anchor) {
        setPosition(null);
        return;
      }
      const rect = anchor.getBoundingClientRect();
      if (!rect.width || !rect.height) {
        setPosition(null);
        return;
      }
      setPosition({
        left: Math.max(16, Math.min(rect.left, window.innerWidth - 336)),
        top: Math.max(16, Math.min(rect.bottom + 12, window.innerHeight - 260)),
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    cardRef.current?.focus();
    return () => {
      anchor?.classList.remove('ring-2', 'ring-brand-400', 'ring-offset-2');
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [index, open, steps]);

  if (!steps.length) return null;

  const persist = (status) => {
    const next = { status, version: 1 };
    if (key) localStorage.setItem(key, JSON.stringify(next));
    setState(next);
  };
  const close = (status = 'dismissed') => {
    persist(status);
    setOpen(false);
    triggerRef.current?.focus();
  };
  const next = () => {
    if (index === steps.length - 1) close('completed');
    else setIndex(index + 1);
  };

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="outline"
        className={className}
        onClick={() => { setIndex(0); setOpen(true); }}
      >
        {state ? 'Replay tour' : 'Show me around'}
      </Button>
      {open && (
        <div
          ref={cardRef}
          tabIndex={-1}
          role="dialog"
          aria-modal="false"
          aria-label={steps[index].title}
          onKeyDown={(event) => { if (event.key === 'Escape') close(); }}
          className={`fixed z-50 w-[min(320px,calc(100vw-32px))] rounded-xl bg-brand-600 p-5 text-white shadow-2xl ${position ? '' : 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'}`}
          style={position || undefined}
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-white/75">Step {index + 1} / {steps.length}</p>
          <h2 className="mt-3 text-lg font-semibold">{steps[index].title}</h2>
          <p className="mt-2 text-sm leading-6 text-white/90">{steps[index].body}</p>
          <div className="mt-5 flex items-center justify-between gap-2">
            <button type="button" className="text-xs underline underline-offset-2" onClick={() => close()} aria-label="Close tour">Close tour</button>
            <div className="flex gap-2">
              {index > 0 && <button type="button" className="rounded-lg border border-white/40 px-3 py-2 text-xs" onClick={() => setIndex(index - 1)}>Back</button>}
              <button type="button" className="rounded-lg bg-white px-4 py-2 text-xs font-semibold text-brand-600" onClick={next}>{index === steps.length - 1 ? 'Finish' : 'Next'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GuidedTour;
